/**
 * Maps an ApiError to user-facing copy (FRONTEND_PLAN §4.9). Keyed on `subCode` first — the domain
 * meaning — then on the top-level `code`, then on the transport kind. Unknown pairs fall back to
 * the server message, and the request id is always available for support.
 */
import { BY_CODE, BY_SUB_CODE, SERVER_WORDED_TITLES } from './error-copy'
import type { ApiError } from './errors'

export interface ErrorDescription {
  readonly title: string
  readonly message: string
  /** Show a retry affordance. */
  readonly retryable: boolean
  /** Seconds the caller should wait before retrying, when the server said so. */
  readonly retryAfterSeconds?: number
  /** The session is gone; the app must return to login. */
  readonly requiresLogin: boolean
  readonly requestId?: string
}

export function describeError(error: ApiError): ErrorDescription {
  const base = {
    ...(error.retryAfterSeconds === undefined
      ? {}
      : { retryAfterSeconds: error.retryAfterSeconds }),
    ...(error.requestId === undefined ? {} : { requestId: error.requestId }),
  }

  if (error.kind === 'network') {
    return {
      title: 'You’re offline',
      message: 'Check your connection and try again.',
      retryable: true,
      requiresLogin: false,
      ...base,
    }
  }
  if (error.kind === 'timeout') {
    return {
      title: 'Timed out',
      message: 'The request took too long. Please try again.',
      retryable: true,
      requiresLogin: false,
      ...base,
    }
  }

  const bySub = error.subCode === undefined ? undefined : BY_SUB_CODE[error.subCode]
  if (bySub !== undefined) return { ...bySub, requiresLogin: false, ...base }

  // A generic business-rule code we have no specific copy for: the backend's own message is
  // written for humans (e.g. "Set a custom_domain before requesting verification.") — with or
  // without a sub-code — so it beats our generic sentence.
  if (
    (error.code === 'CONFLICT' || error.code === 'UNPROCESSABLE') &&
    error.message.trim() !== ''
  ) {
    const generic = BY_CODE[error.code]
    return {
      title: generic?.title ?? 'Can’t do that',
      message: error.message,
      retryable: false,
      requiresLogin: false,
      ...base,
    }
  }

  const worded = SERVER_WORDED_TITLES[error.code]
  if (worded !== undefined) {
    return {
      title: worded,
      message: error.message.trim() === '' ? worded : error.message,
      retryable: false,
      requiresLogin: false,
      ...base,
    }
  }

  const byCode = BY_CODE[error.code]
  if (byCode !== undefined) {
    const { requiresLogin = false, ...rest } = byCode
    return { ...rest, requiresLogin, ...base }
  }

  return {
    title: 'Something went wrong',
    message: error.message,
    retryable: error.status >= 500,
    requiresLogin: false,
    ...base,
  }
}
